import {
  Badge,
  Button,
  Card,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconLock, IconPlus } from '@tabler/icons-react';
import { useMemo } from 'react';
import { LocalhostOnlyBadge } from '../../../../components/LocalhostOnlyBadge';
import type {
  ClientPortalCoverageSummary,
  ClientPortalInsurancePriority,
} from '../../utils/clientPortalFhirTypes';
import {
  CLIENT_PORTAL_INSURANCE_PRIORITIES,
  formatBillingDate,
  formatMoney,
  insurancePriorityBadgeColor,
  insurancePriorityDescription,
  insurancePriorityLabel,
  insurancePriorityLockReason,
  organizeCoverageByPriority,
} from '../../utils/portalBillingModels';

export interface ClientPortalInsurancePlansSectionProps {
  coverageSummaries: ClientPortalCoverageSummary[];
  onEditCoverage: (coverage: ClientPortalCoverageSummary) => void;
  onAddCoverage: (priority: ClientPortalInsurancePriority) => void;
  localhostOnly?: boolean;
}

function InsurancePlanSlotCard({
  priority,
  coverage,
  lockReason,
  onEdit,
  onAdd,
}: {
  priority: ClientPortalInsurancePriority;
  coverage?: ClientPortalCoverageSummary;
  lockReason?: string;
  onEdit: () => void;
  onAdd: () => void;
}): JSX.Element {
  const priorityLabel = insurancePriorityLabel(priority);
  const badgeColor = insurancePriorityBadgeColor(priority);

  if (!coverage) {
    return (
      <Paper
        withBorder
        p="lg"
        radius="md"
        h="100%"
        style={{ borderStyle: 'dashed' }}
        bg="gray.0"
      >
        <Stack gap="md" h="100%">
          <Badge size="lg" variant="light" color={badgeColor} w="fit-content">
            {priorityLabel}
          </Badge>
          <Text size="sm" c="dimmed">
            {insurancePriorityDescription(priority)}
          </Text>
          <Stack gap={4} mt="auto">
            <Group gap={6} wrap="nowrap">
              {lockReason && <IconLock size={14} color="var(--mantine-color-gray-6)" />}
              <Text size="sm" fw={600} c="gray.6">
                {lockReason ?? `No ${priorityLabel.toLowerCase()} insurance on file`}
              </Text>
            </Group>
            <Button
              size="xs"
              variant="light"
              color="gold"
              leftSection={lockReason ? <IconLock size={14} /> : <IconPlus size={14} />}
              onClick={onAdd}
              disabled={lockReason != null}
            >
              Add {priorityLabel.toLowerCase()} plan
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  return (
    <Card withBorder padding="lg" radius="md" h="100%">
      <Stack gap="sm" h="100%">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Badge size="lg" variant="filled" color={badgeColor}>
            {priorityLabel}
          </Badge>
          <Button size="xs" variant="light" color="gold" onClick={onEdit}>
            Update insurance
          </Button>
        </Group>

        <Text size="xs" c="dimmed">
          {insurancePriorityDescription(priority)}
        </Text>

        <Text fw={600} size="sm" lineClamp={1} mt="xs">
          {coverage.payerName}
        </Text>
        {coverage.planName && (
          <Text size="sm" c="dimmed" lineClamp={1}>
            {coverage.planName}
          </Text>
        )}

        <Stack gap={4}>
          {coverage.memberIdMasked && (
            <Group gap="xs" wrap="nowrap">
              <Text size="sm" c="gray.6" w={100}>
                Member ID
              </Text>
              <Text size="sm" fw={500}>
                {coverage.memberIdMasked}
              </Text>
            </Group>
          )}
          {coverage.groupNumberMasked && (
            <Group gap="xs" wrap="nowrap">
              <Text size="sm" c="gray.6" w={100}>
                Group #
              </Text>
              <Text size="sm" fw={500}>
                {coverage.groupNumberMasked}
              </Text>
            </Group>
          )}
          {coverage.copayCents != null && (
            <Group gap="xs" wrap="nowrap">
              <Text size="sm" c="gray.6" w={100}>
                Copay
              </Text>
              <Text size="sm" fw={500}>
                {formatMoney(coverage.copayCents)}
              </Text>
            </Group>
          )}
          {coverage.deductibleRemainingCents != null && (
            <Group gap="xs" wrap="nowrap">
              <Text size="sm" c="gray.6" w={100}>
                Deductible left
              </Text>
              <Text size="sm" fw={500}>
                {formatMoney(coverage.deductibleRemainingCents)}
              </Text>
            </Group>
          )}
        </Stack>

        <Group gap="xs" mt="auto">
          <Badge size="sm" variant="light" color={coverage.status === 'active' ? 'green' : 'gray'}>
            {coverage.status === 'active' ? 'Active' : coverage.status === 'inactive' ? 'Inactive' : 'Unknown'}
          </Badge>
          {coverage.lastVerified && (
            <Text size="xs" c="gray.6">
              Verified {formatBillingDate(coverage.lastVerified)}
            </Text>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

export function ClientPortalInsurancePlansSection({
  coverageSummaries,
  onEditCoverage,
  onAddCoverage,
  localhostOnly,
}: ClientPortalInsurancePlansSectionProps): JSX.Element {
  const coverageByPriority = useMemo(
    () => organizeCoverageByPriority(coverageSummaries),
    [coverageSummaries],
  );
  const filledCount = CLIENT_PORTAL_INSURANCE_PRIORITIES.filter(
    (priority) => coverageByPriority[priority] != null,
  ).length;

  return (
    <Stack gap="md">
      <div>
        <Group gap="sm" align="center" wrap="wrap">
          <Title order={3}>Insurance plans</Title>
          {localhostOnly ? <LocalhostOnlyBadge /> : null}
        </Group>
        <Text size="sm" c="dimmed" mt={4}>
          You can have up to three insurance plans on file — one primary, one secondary, and one tertiary.
        </Text>
      </div>

      <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
        {CLIENT_PORTAL_INSURANCE_PRIORITIES.map((priority) => {
          const coverage = coverageByPriority[priority];
          return (
            <InsurancePlanSlotCard
              key={priority}
              priority={priority}
              coverage={coverage}
              lockReason={insurancePriorityLockReason(priority, coverageByPriority)}
              onEdit={() => coverage && onEditCoverage(coverage)}
              onAdd={() => onAddCoverage(priority)}
            />
          );
        })}
      </SimpleGrid>

      <Text size="xs" c="dimmed">
        {filledCount} of 3 plan slots in use
      </Text>
    </Stack>
  );
}
